import AcceptConflictBold from '@/assets/icons/AcceptConflictBold';
import Seat from '@/assets/icons/Seat';
import { mapInzetPositieToShortString } from '@/components/detailPanel/utils/utils';
import useClientState from '@/state/useClientState';
import { Eenheid } from '@/types/werklijnenInline';

import styles from '../DetailPanel.module.css';
import samenstellingStyles from './Samenstelling.module.css';

type SamenstellingRowProps = {
  inzetId?: string;
  werklijnId?: string;
  eenheid?: Eenheid;
  samenstelling: string;
  geaccepteerdConflict?: boolean;
  geplandeTreinSamenstelling?: {
    samenstelling: string;
    aantalZitplaatsen: number;
    volNorm: number;
  };
  aantalZitplaatsen?: number;
  treinnummer?: string | number;
  inzetPositie?: string | number;
  showPositieShort?: boolean;
  handleInzetClick?: (inzetId?: string, werklijnId?: string) => void;
};

export default function SamenstellingRow({
  inzetId,
  werklijnId,
  eenheid,
  samenstelling,
  geplandeTreinSamenstelling,
  geaccepteerdConflict,
  aantalZitplaatsen,
  treinnummer,
  inzetPositie,
  showPositieShort = false,
  handleInzetClick,
}: Readonly<SamenstellingRowProps>) {
  const setMaterieelPanelData = useClientState((state) => state.setMaterieelPanelData);
  const setShowMaterieelPanel = useClientState((state) => state.setShowMaterieelPanel);

  const isManco = eenheid?.nummer === 'MANCO';

  let samenstellingColor = 'inherit';
  if (geplandeTreinSamenstelling) {
    samenstellingColor = 'var(--mwd-color-treintaak-background-verkeerd-mat)';
  } else if (isManco) {
    samenstellingColor = 'var(--tractie-color-content-placeholder)';
  }

  const geplandeTreinSamenstellingLabel = geplandeTreinSamenstelling?.samenstelling
    ? `${geplandeTreinSamenstelling.samenstelling.charAt(0)}...-${geplandeTreinSamenstelling.samenstelling.slice(-1)}`
    : null;

  const handleMaterieelClick = () => {
    if (setMaterieelPanelData && eenheid && !isManco) {
      setMaterieelPanelData(eenheid);
      setShowMaterieelPanel();
    }
  };

  return (
    <div className={styles.flexRow}>
      <span
        data-testid={`${inzetId}-eenheidnummer`}
        className={samenstellingStyles.eenheidNummer}
        onClick={handleMaterieelClick}
        style={{
          fontWeight: 600,
          cursor: !isManco ? 'pointer' : 'default',
          color: !isManco
            ? 'var(--tractie-color-content-interactive-default)'
            : 'var(--tractie-color-content-placeholder)',
        }}
      >
        {isManco ? 'Manco' : eenheid?.nummer}
        {geaccepteerdConflict && (
          <AcceptConflictBold
            data-icon='AcceptConflict'
            height={16}
            width={16}
            fill='var(--tractie-color-content-default)'
          />
        )}
      </span>
      <span style={{ minWidth: '75px' }}>
        {geplandeTreinSamenstelling && (
          <span
            style={{
              marginRight: '4px',
              color: geplandeTreinSamenstelling ? 'var(--tractie-color-content-placeholder)' : 'inherit',
              textDecoration: geplandeTreinSamenstelling ? 'line-through' : 'none',
            }}
          >
            {geplandeTreinSamenstellingLabel}
          </span>
        )}
        <span
          style={{
            color: samenstellingColor,
            textDecoration: isManco ? 'line-through' : 'none',
          }}
          data-testid={`${inzetId}-materieel-samenstelling`}
        >
          {samenstelling || 'Onbekend'}
        </span>
      </span>
      <span>
        {aantalZitplaatsen ? (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '2px',
              minWidth: '68px',
              color: isManco ? 'var(--tractie-color-content-placeholder)' : 'inherit',
              textDecoration: isManco ? 'line-through' : 'none',
            }}
          >
            <Seat style={{ fontSize: '16px', fill: 'var(--tractie-color-content-emphasis)' }} />
            {geplandeTreinSamenstelling && (
              <span
                style={{
                  color: geplandeTreinSamenstelling ? 'var(--tractie-color-content-placeholder)' : 'inherit',
                  textDecoration: geplandeTreinSamenstelling ? 'line-through' : 'none',
                }}
              >
                {geplandeTreinSamenstelling.aantalZitplaatsen}
              </span>
            )}
            <span data-testid={`${inzetId}-zitplaatsen`}>{aantalZitplaatsen}</span>
          </span>
        ) : null}
      </span>
      <div
        className={werklijnId ? samenstellingStyles.inzetNummer : undefined}
        onClick={handleInzetClick && werklijnId ? () => handleInzetClick(inzetId, werklijnId) : undefined}
        style={{
          display: 'flex',
        }}
      >
        <span data-testid={`${inzetId}-treinnummer`}>
          {treinnummer}
          {showPositieShort && mapInzetPositieToShortString(inzetPositie as number)}
          {!showPositieShort && inzetPositie ? `-${inzetPositie}` : ''}
        </span>
      </div>
    </div>
  );
}
